import React from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';

interface OnPageContentProps {
  onTooltipShow: (title: string, content: string) => void;
  onNavigate: (section: string) => void;
  darkMode?: boolean;
}

export default function OnPageContent({ onTooltipShow, onNavigate, darkMode }: OnPageContentProps) {
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: 'Implementation Hub / On-Page Content',
    routePath: '/dashboard#on-page-content',
  });

  return (
    <div className={`space-y-6 ${darkMode ? 'dark text-white' : 'text-gray-900'}`}>

      {/* Header Area */}
      <div className="mb-4">
        <button
          onClick={() => onNavigate('implementation-overview')}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">On-Page Content</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Optimize elements directly on your website for better search visibility</p>
        </div>
      </div>

      {/* Grid Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Card 1: Image Optimizer */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border border-blue-100 dark:border-blue-800">
                <i className="ri-image-edit-line text-2xl text-blue-600 dark:text-blue-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Image Optimizer</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Scan your website for oversized images or compress and optimise new ones.</p>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-search-eye-line"></i> Scan Website Images
                  </span>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300">
                    <i className="ri-upload-cloud-line"></i> Upload & Optimise Images
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => onNavigate('image-optimizer')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Card 2: Meta Title Generator */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-teal-50 dark:bg-teal-900/30 rounded-xl flex items-center justify-center border border-teal-100 dark:border-teal-800">
                <i className="ri-code-s-slash-line text-2xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Meta Title Generator</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate and audit all meta tags — titles, descriptions, Open Graph, Twitter cards, canonical tags, and more.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {['Title', 'Meta Description', 'Robots', 'Canonical', 'Viewport', 'OG Tags', 'Twitter Card', 'ALT Text', 'hreflang'].map(chip => (
                <span key={chip} className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 text-[11px] font-medium rounded-md">
                  {chip}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-end justify-end mt-6">
            <button
              onClick={() => onNavigate('meta-title-generator')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Card 3: Blog */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center border border-emerald-100 dark:border-emerald-800">
                <i className="ri-article-line text-2xl text-emerald-600 dark:text-emerald-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Blog</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Discover trending topics, customize content with unique insights, and generate SEO-optimized blog posts.</p>
              </div>
            </div>
          </div>

          <div className="flex items-end justify-end mt-6">
            <button
              onClick={() => onNavigate('blog')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Card 4: FAQ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col justify-between hover:shadow-md transition-shadow">
          <div>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex-shrink-0 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center border border-indigo-100 dark:border-indigo-800">
                <i className="ri-question-answer-line text-2xl text-indigo-600 dark:text-indigo-400"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">FAQ</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate and manage SEO-optimized FAQ sections for your website pages with site hierarchy selection.</p>
              </div>
            </div>
          </div>

          <div className="flex items-end justify-end mt-6">
            <button
              onClick={() => onNavigate('faq')}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              Get Started <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
